import React from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

import { IResourceFile } from '../../store/ResourcesSlice';

interface ResourcesExplorerMiniProps {
  title?: string;
  files: IResourceFile[];
  history: string[];
  isLoading?: boolean;
  selectedResources?: IResourceFile[];
  hiddenFiles?: Array<string>;
  onFolderClick?: (file: IResourceFile) => void;
  onFileClick?: (file: IResourceFile) => void;
  onBackClick?: () => void;
  onSelectedFileDelete?: (file: IResourceFile) => void;
}

// STYLES
const defaultHeadingProps: TypographyProps = {
  fontWeight: 600,
};

const ResourcesExplorerMini: React.FC<ResourcesExplorerMiniProps> = ({
  title = 'Resource Explorer',
  files,
  history,
  isLoading = false,
  selectedResources = [],
  onFolderClick,
  onFileClick,
  onBackClick,
  onSelectedFileDelete,
  hiddenFiles = [],
}) => {
  const resourceFolders = files.filter(
    (file) => file.mimeType === 'application/vnd.google-apps.folder'
  );
  const resourceFiles = files.filter(
    (file) =>
      file.mimeType !== 'application/vnd.google-apps.folder' &&
      !hiddenFiles.includes(file.id)
  );

  return (
    <div>
      <Stack
        direction="row"
        alignItems="center"
        style={{ marginBottom: '15px' }}
      >
        <Typography {...defaultHeadingProps}>{title}</Typography>
        {isLoading && (
          <CircularProgress
            size={16}
            color="primary"
            style={{ marginLeft: '5px' }}
          />
        )}
      </Stack>

      {history.length > 1 && (
        <div style={{ marginBottom: '15px' }}>
          <Button
            variant="text"
            startIcon={<ArrowCircleLeftIcon />}
            onClick={onBackClick}
            disabled={isLoading}
          >
            Back
          </Button>
        </div>
      )}

      {selectedResources.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          {selectedResources.map((selectedResource, index) => (
            <Chip
              key={index}
              label={selectedResource.title}
              onDelete={
                onSelectedFileDelete
                  ? () => onSelectedFileDelete(selectedResource)
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {resourceFolders.length > 0 && (
        <>
          <Stack style={{ marginBottom: '10px' }}>
            <Typography variant="subtitle2" color="secondary" fontWeight={600}>
              Folders
            </Typography>
            <Divider />
          </Stack>
          <List>
            {resourceFolders.map((resourceFolder, index) => {
              return (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    disabled={isLoading}
                    onClick={
                      onFolderClick
                        ? () => onFolderClick(resourceFolder)
                        : undefined
                    }
                  >
                    <ListItemIcon>
                      <img
                        alt="folder"
                        src={`${process.env.PUBLIC_URL}/images/resourceLib/folder.svg`}
                        width="25px"
                        height="25px"
                      />
                    </ListItemIcon>
                    <ListItemText primary={resourceFolder.title} />
                    <KeyboardArrowRightIcon />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {resourceFiles.length > 0 && (
        <>
          <Stack style={{ marginBottom: '10px' }}>
            <Typography variant="subtitle2" color="secondary" fontWeight={600}>
              Files
            </Typography>
            <Divider />
          </Stack>
          <List>
            {resourceFiles.map((resourceFile, index) => {
              const isSelected = selectedResources.some(
                (f) => f.id === resourceFile.id
              );

              let _src = `${process.env.PUBLIC_URL}/images/resourceLib/folder.svg`;

              switch (resourceFile.mimeType) {
                case 'application/pdf':
                  _src = `${process.env.PUBLIC_URL}/images/resourceLib/pdf.svg`;
                  break;
                case 'image/jpeg':
                  _src = `${process.env.PUBLIC_URL}/images/resourceLib/jpg.svg`;
                  break;
                default:
                  _src = `${process.env.PUBLIC_URL}/images/resourceLib/folder.svg`;
                  break;
              }

              return (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    disabled={isLoading}
                    onClick={
                      onFileClick ? () => onFileClick(resourceFile) : undefined
                    }
                  >
                    <ListItemIcon>
                      <img alt="folder" src={_src} width="25px" height="25px" />
                    </ListItemIcon>
                    <ListItemText primary={resourceFile.title} />
                    {isSelected ? (
                      <RadioButtonCheckedIcon color="primary" />
                    ) : (
                      <RadioButtonUncheckedIcon />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {resourceFolders.length === 0 &&
      resourceFiles.length === 0 &&
      !isLoading ? (
        <div style={{ marginTop: '-15px' }}>
          <Typography variant="caption" color="secondary">
            No resources are available at the moment.
          </Typography>
        </div>
      ) : null}
    </div>
  );
};

export default ResourcesExplorerMini;
